(() => {
  "use strict";

  const root = document.querySelector("[data-customizer-root]");
  if (!root) return;

  const apiBase = document.body.dataset.customizationApi.replace(/\/+$/, "");
  const isLocalDemo = ["127.0.0.1", "localhost"].includes(window.location.hostname);
  const supportedLocales = new Set(["en-US", "de-DE", "it-IT", "es-ES", "fr-FR"]);
  const requestedLocale = localStorage.getItem("formOfUsLocale") || document.documentElement.lang || "en-US";
  const locale = supportedLocales.has(requestedLocale) ? requestedLocale : "en-US";
  const handle = decodeURIComponent(location.pathname.match(/^\/products\/([^/?#]+)/)?.[1] || new URLSearchParams(location.search).get("handle") || "custom-funko-figurine-from-photo");

  const nodes = {
    title: document.querySelector("[data-product-title]"),
    breadcrumb: document.querySelector("[data-product-breadcrumb]"),
    category: document.querySelector("[data-product-category]"),
    image: document.querySelector("[data-product-image]"),
    thumb: document.querySelector("[data-product-thumb]"),
    price: document.querySelector("[data-product-price]"),
    compare: document.querySelector("[data-product-compare]"),
    configurations: document.querySelector("[data-configuration-options]"),
    selectedConfiguration: document.querySelector("[data-selected-configuration]"),
    styles: document.querySelector("[data-style-options]"),
    selectedStyle: document.querySelector("[data-selected-style]"),
    selectedSize: document.querySelector("[data-selected-size]"),
    input: document.querySelector("[data-photo-input]"),
    sourcePreview: document.querySelector("[data-source-preview]"),
    uploadCopy: document.querySelector("[data-upload-copy]"),
    note: document.querySelector("[data-customer-note]"),
    turnstileWrap: document.querySelector("[data-turnstile-wrap]"),
    turnstileWidget: document.querySelector("[data-turnstile-widget]"),
    status: document.querySelector("[data-customizer-status]"),
    generate: document.querySelector("[data-generate-button]"),
    cartButton: document.querySelector("[data-cart-button]"),
    buyNow: document.querySelector("[data-buy-now]"),
    dialog: document.querySelector("[data-preview-dialog]"),
    previewGrid: document.querySelector("[data-preview-grid]"),
    stageImage: document.querySelector("[data-stage-image]"),
    stageLoader: document.querySelector("[data-stage-loader]"),
    stageEmpty: document.querySelector("[data-stage-empty]"),
    stageLabel: document.querySelector("[data-stage-label]"),
    stageStatus: document.querySelector("[data-stage-status]"),
    stageRetry: document.querySelector("[data-stage-retry]"),
    confirm: document.querySelector("[data-confirm-selection]"),
    historyDialog: document.querySelector("[data-history-dialog]"),
    historyList: document.querySelector("[data-history-list]"),
  };

  const state = {
    product: null,
    dictionary: null,
    file: null,
    sourceObjectUrl: "",
    sessionId: "",
    sessionToken: "",
    tasks: [],
    selectedTaskId: "",
    activeSlot: 1,
    batchKey: "",
    retryKeys: {},
    pollTimer: null,
    turnstileToken: "",
    turnstileWidgetId: "",
    securityChallenge: "",
    securityProof: null,
    securityReady: false,
    generationMode: "enhanced",
    autoStartRequested: false,
  };

  const money = (value) => new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);

  const themeAsset = (name) => window.FORM_OF_US_THEME_ASSETS?.[name] || `/assets/${name}`;
  const stableKey = (prefix) => `${prefix}:${crypto.randomUUID()}`;
  const getNested = (object, key) => key.split(".").reduce((current, part) => current && current[part], object);
  const copy = (key, fallback) => getNested(state.dictionary, key) || fallback;
  const optionLabel = (slot) => copy(`preview.option_${slot}`, `Option ${slot}`);
  const statusLabel = (status) => copy(`preview.status_${status}`, String(status || "waiting").replace(/_/g, " "));

  function applyLocaleCopy() {
    document.documentElement.lang = locale;
    document.querySelectorAll("[data-i18n]").forEach((node) => {
      const value = getNested(state.dictionary, node.dataset.i18n);
      if (typeof value === "string") node.textContent = value;
    });
    if (nodes.note) nodes.note.placeholder = copy("product.note_placeholder", nodes.note.placeholder);
  }

  async function loadDictionary() {
    if (window.FORM_OF_US_LOCALES?.[locale]) return window.FORM_OF_US_LOCALES[locale];
    const response = await fetch(`/locales/${encodeURIComponent(locale)}.json`);
    if (!response.ok) throw new Error("locale_load_failed");
    return response.json();
  }

  function setStatus(message, error = false) {
    nodes.status.textContent = message;
    nodes.status.classList.toggle("is-error", error);
  }

  function stagedError(key, fallback, error) {
    const original = String(error?.message || "");
    const message = error instanceof TypeError || /fetch|network|load failed/i.test(original)
      ? copy(key, fallback)
      : original || copy(key, fallback);
    const staged = new Error(message);
    staged.formOfUsStage = key;
    return staged;
  }

  function updateGenerateAvailability() {
    nodes.generate.disabled = !state.file || (!isLocalDemo && !state.turnstileToken && !state.securityReady);
  }

  function proofMatches(bytes, difficulty) {
    const wholeBytes = Math.floor(difficulty / 8);
    const remainingBits = difficulty % 8;
    for (let index = 0; index < wholeBytes; index += 1) if (bytes[index] !== 0) return false;
    return !remainingBits || (bytes[wholeBytes] & (0xff << (8 - remainingBits))) === 0;
  }

  async function prepareFallbackSecurity() {
    state.securityReady = false;
    updateGenerateAvailability();
    const payload = await api("/api/shopify/customization/security-challenge");
    const encoder = new TextEncoder();
    for (let counter = 0; counter <= 100000000; counter += 1) {
      const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", encoder.encode(`${payload.challenge}:${counter}`)));
      if (proofMatches(digest, Number(payload.difficulty || 12))) {
        state.securityChallenge = payload.challenge;
        state.securityProof = counter;
        state.securityReady = true;
        nodes.turnstileWrap.hidden = true;
        updateGenerateAvailability();
        if (state.file && state.autoStartRequested) {
          state.autoStartRequested = false;
          startGeneration();
        }
        return;
      }
    }
    throw new Error("Secure preview check could not be completed.");
  }

  function loadTurnstileScript() {
    if (window.turnstile) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-form-of-us-turnstile="1"]');
      if (existing) {
        existing.addEventListener("load", resolve, { once: true });
        existing.addEventListener("error", reject, { once: true });
        return;
      }
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.formOfUsTurnstile = "1";
      script.addEventListener("load", resolve, { once: true });
      script.addEventListener("error", () => reject(new Error("Security check could not be loaded.")), { once: true });
      document.head.append(script);
    });
  }

  async function initializeTurnstile() {
    if (isLocalDemo) return;
    const config = await api("/api/shopify/customization/config");
    if (!config.turnstile_site_key) return prepareFallbackSecurity();
    nodes.turnstileWrap.hidden = false;
    try { await loadTurnstileScript(); } catch { return prepareFallbackSecurity(); }
    state.turnstileWidgetId = window.turnstile.render(nodes.turnstileWidget, {
      sitekey: config.turnstile_site_key,
      theme: "light",
      callback: (token) => {
        state.turnstileToken = token;
        updateGenerateAvailability();
        if (state.file) setStatus(copy("product.generate", "Choose from four online previews when you are ready."));
        if (state.file && state.autoStartRequested) {
          state.autoStartRequested = false;
          startGeneration();
        }
      },
      "expired-callback": () => {
        state.turnstileToken = "";
        updateGenerateAvailability();
        setStatus(copy("product.error_generic", "Security check expired. Please complete it again."), true);
      },
      "error-callback": () => {
        state.turnstileToken = "";
        prepareFallbackSecurity().catch((error) => setStatus(error.message, true));
      },
    });
    updateGenerateAvailability();
  }

  function authHeaders(idempotencyKey = "") {
    const headers = { "content-type": "application/json" };
    if (state.sessionToken) headers["x-customization-session"] = state.sessionToken;
    if (idempotencyKey) headers["x-idempotency-key"] = idempotencyKey;
    return headers;
  }

  async function api(path, options = {}) {
    const response = await fetch(apiBase + path, options);
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const fallback = copy("product.error_generic", `Request failed (${response.status})`);
      throw new Error(locale === "en-US" ? payload?.error?.message || fallback : fallback);
    }
    return payload;
  }

  function saveSession() {
    if (!state.sessionId || !state.sessionToken) return;
    localStorage.setItem("formOfUsCustomization", JSON.stringify({
      sessionId: state.sessionId,
      sessionToken: state.sessionToken,
      productHandle: handle,
      createdAt: Date.now(),
    }));
  }

  function renderProduct(product) {
    state.product = product;
    const title = product.title[locale] || product.title["en-US"];
    const imageUrl = themeAsset(product.image || `product-${product.handle}-v1.png`);
    document.title = `${title} — FORM OF US`;
    nodes.title.textContent = title;
    document.querySelectorAll("[data-product-title]").forEach((node) => { node.textContent = title; });
    nodes.breadcrumb.textContent = title;
    nodes.category.textContent = product.category.replace(/_/g, " ");
    nodes.image.src = imageUrl;
    nodes.image.alt = title;
    nodes.thumb.src = imageUrl;
    const gallery = Array.isArray(product.gallery) && product.gallery.length ? product.gallery : [product.image];
    const galleryStories = [
      { title: copy("v3.gallery_main_title", "Photo to finished keepsake"), body: copy("v3.gallery_main_body", "See the source, finished piece and closed FORM OF US box together.") },
      { title: copy("v3.gallery_details_title", "Designed around the details"), body: copy("v3.gallery_details_body", "Hair, clothing, markings and colors are interpreted from your photo.") },
      { title: copy("v3.gallery_steps_title", "Scale and everyday use"), body: copy("v3.gallery_steps_body", "See the finished keepsake in a hand, on a desk or attached to a bag so its scale is easy to understand.") },
      { title: copy("v3.gallery_receive_title", "Ready to give"), body: copy("v3.gallery_receive_body", "Your approved design is packed in a closed sky-blue and lavender FORM OF US toy box.") },
    ];
    const thumbs = document.querySelector("[data-product-thumbs]");
    if (thumbs) thumbs.innerHTML = gallery.map((asset, index) => `<button class="${index === 0 ? "active" : ""}" type="button" data-gallery-image="${themeAsset(asset)}" data-gallery-alt="${title.replace(/"/g, "&quot;")} — view ${index + 1}" data-gallery-title="${galleryStories[index % galleryStories.length].title.replace(/"/g, "&quot;")}" data-gallery-body="${galleryStories[index % galleryStories.length].body.replace(/"/g, "&quot;")}" aria-label="View product image ${index + 1}"><img ${index === 0 ? "data-product-thumb" : ""} src="${themeAsset(asset)}" alt="${title.replace(/"/g, "&quot;")} — view ${index + 1}"><span>${galleryStories[index % galleryStories.length].title}</span></button>`).join("");
    nodes.price.textContent = money(product.price);
    document.querySelectorAll("[data-product-price]").forEach((node) => { node.textContent = money(product.price); });
    nodes.compare.textContent = money(product.compare_at_price);
    if (product.product_type === "premium_gift_box") {
      document.body.classList.add("is-packaging-product");
      document.querySelectorAll(".option-fieldset, .customization-box, [data-generate-button], [data-buy-now], .sticky-product-bar").forEach((node) => { node.hidden = true; });
      const lead = document.querySelector(".product-lead");
      if (lead) lead.textContent = copy("v3.packaging_product_body", "A closed, gift-ready sky-blue FORM OF US collector box. Add it to a personalized gift or order it separately.");
      if (nodes.cartButton) {
        nodes.cartButton.disabled = false;
        nodes.cartButton.textContent = copy("v3.packaging_add", "Add premium gift box to cart");
      }
      const delivery = document.querySelector(".delivery-note");
      if (delivery) delivery.textContent = copy("v3.packaging_delivery", "Ships as a separate gift-ready packaging item.");
      return;
    }
    const configurationSets = {
      pet_keychain: [
        ["1 pet", 0, 1], ["2 pets", 0, 2], ["Dog", 0, 1], ["Cat", 0, 1], ["Person + pet", 1, 1],
      ],
      pet_figurine: [
        ["1 pet", 0, 1], ["2 pets", 0, 2], ["Dog", 0, 1], ["Cat", 0, 1], ["Person + pet", 1, 1],
      ],
      crystal_globe: [
        ["1 person", 1, 0], ["Couple", 2, 0], ["1 pet", 0, 1], ["Family", 4, 0], ["Person + pet", 1, 1],
      ],
      building_block_figurine: [
        ["1 person", 1, 0], ["2 people", 2, 0], ["Couple", 2, 0], ["Family", 4, 0], ["Celebration", 2, 0],
      ],
      couple_figurine: [
        ["Couple", 2, 0], ["2 people", 2, 0], ["Couple + pet", 2, 1], ["Family", 4, 0],
      ],
      funko_figurine: [
        ["1 person", 1, 0], ["2 people", 2, 0], ["Couple", 2, 0], ["1 pet", 0, 1], ["Person + pet", 1, 1],
      ],
    };
    const configurations = configurationSets[product.product_type] || configurationSets.funko_figurine;
    const configurationAssets = {
      funko_figurine: ["product-custom-funko-figurine-from-photo-v3.webp", "product-custom-wedding-couple-figurine-v3.webp", "product-custom-wedding-couple-figurine-v3-2.webp", "product-custom-pet-figurine-from-photo-v3.webp", "category-family-v1.png"],
      couple_figurine: ["product-custom-wedding-couple-figurine-v3.webp", "product-custom-wedding-couple-figurine-v3-2.webp", "category-family-v1.png", "category-couples-v1.png"],
      pet_keychain: ["product-custom-pet-figurine-keychain-v3.webp", "product-custom-pet-figurine-keychain-v3-2.webp", "product-custom-pet-figurine-keychain-v3.webp", "product-custom-pet-figurine-keychain-v3-3.webp", "category-family-v1.png"],
      pet_figurine: ["product-custom-pet-figurine-from-photo-v3.webp", "product-custom-pet-figurine-from-photo-v3-2.webp", "product-custom-pet-figurine-from-photo-v3.webp", "product-custom-pet-figurine-from-photo-v3-3.webp", "category-family-v1.png"],
      crystal_globe: ["product-custom-photo-crystal-globe-v3.webp", "product-custom-photo-crystal-globe-v3-2.webp", "product-custom-photo-crystal-globe-v3-3.webp", "category-family-v1.png", "product-custom-photo-crystal-globe-v3-4.webp"],
      building_block_figurine: ["product-custom-photo-building-block-figure-v3.webp", "product-custom-photo-building-block-figure-v3-2.webp", "product-custom-photo-building-block-figure-v3-2.webp", "category-family-v1.png", "product-custom-photo-building-block-figure-v3-4.webp"],
    }[product.product_type] || gallery;
    if (nodes.configurations) nodes.configurations.innerHTML = configurations.map(([label, people, pets], index) => `
      <label>
        <input type="radio" name="configuration" value="${label}" data-people="${people}" data-pets="${pets}" ${index === 0 ? "checked" : ""}>
        <span><img src="${themeAsset(configurationAssets[index % configurationAssets.length])}" alt="${label}"><strong>${label}</strong></span>
      </label>`).join("");
    if (nodes.selectedConfiguration) nodes.selectedConfiguration.textContent = configurations[0][0];
    nodes.styles.innerHTML = product.style_options.map((style, index) => `
      <label>
        <input type="radio" name="style" value="${style.replace(/"/g, "&quot;")}" ${index === 0 ? "checked" : ""}>
        <span><img src="${themeAsset(gallery[index % gallery.length])}" alt=""><strong>${style}</strong></span>
      </label>`).join("");
    nodes.selectedStyle.textContent = product.style_options[0];

    const featureAssets = {
      wide: gallery[1] || gallery[0],
      before: gallery[0],
      after: gallery[2] || gallery[0],
      compare: gallery[3] || gallery[0],
    };
    const featureWide = document.querySelector("[data-feature-wide]");
    const featureBefore = document.querySelector("[data-feature-before]");
    const featureAfter = document.querySelector("[data-feature-after]");
    const compareOurs = document.querySelector("[data-compare-ours]");
    if (featureWide) featureWide.src = themeAsset(featureAssets.wide);
    if (featureBefore) featureBefore.src = themeAsset(featureAssets.before);
    if (featureAfter) featureAfter.src = themeAsset(featureAssets.after);
    if (compareOurs) compareOurs.src = themeAsset(featureAssets.compare);
    const featureCopyKeys = {
      pet_keychain: ["pet_look_title", "pet_look_body"],
      pet_figurine: ["pet_look_title", "pet_look_body"],
      crystal_globe: ["crystal_look_title", "crystal_look_body"],
      building_block_figurine: ["block_look_title", "block_look_body"],
      couple_figurine: ["couple_look_title", "couple_look_body"],
      funko_figurine: ["look_title", "look_body"],
    };
    const [featureTitleKey, featureBodyKey] = featureCopyKeys[product.product_type] || featureCopyKeys.funko_figurine;
    const featureTitle = document.querySelector("[data-feature-title]");
    const featureBody = document.querySelector("[data-feature-body]");
    if (featureTitle) featureTitle.textContent = copy(`v3.${featureTitleKey}`, featureTitle.textContent);
    if (featureBody) featureBody.textContent = copy(`v3.${featureBodyKey}`, featureBody.textContent);
    const proof = document.querySelector("[data-proof-carousel]");
    if (proof) proof.innerHTML = gallery.map((asset, index) => `<figure><img loading="lazy" src="${themeAsset(asset)}" alt="${title.replace(/"/g, "&quot;")} — product example ${index + 1}"><figcaption>${galleryStories[index % galleryStories.length].title}</figcaption></figure>`).join("");
  }

  function currentStyle() {
    return document.querySelector('input[name="style"]:checked')?.value || state.product.style_options[0];
  }

  function currentConfiguration() {
    const checked = document.querySelector('input[name="configuration"]:checked');
    return {
      label: checked?.value || "1 person",
      people: Number(checked?.dataset.people || state.product?.people || 0),
      pets: Number(checked?.dataset.pets || state.product?.pets || 0),
    };
  }

  function updateSlot(slotNumber, task = {}) {
    const card = nodes.previewGrid.querySelector(`[data-slot="${slotNumber}"]`);
    if (!card) return;
    const image = card.querySelector("img");
    const status = card.querySelector(".preview-slot__meta span");
    const taskStatus = task.status || "waiting";
    card.dataset.taskId = task.task_id || "";
    card.classList.toggle("is-ready", taskStatus === "completed");
    card.classList.toggle("is-failed", taskStatus === "failed");
    card.classList.toggle("selected", Boolean(task.selected) || task.task_id === state.selectedTaskId);
    status.textContent = statusLabel(taskStatus);
    if (task.preview_url) {
      image.src = task.preview_url;
      image.hidden = false;
    } else if (isLocalDemo && ["completed", "failed"].includes(taskStatus)) {
      image.src = themeAsset(state.product.gallery?.[slotNumber - 1] || state.product.image || `product-${handle}-v1.png`);
      image.hidden = taskStatus !== "completed";
    } else {
      image.hidden = true;
    }
  }

  function renderTasks(tasks) {
    state.tasks = [...tasks].sort((a, b) => Number(a.slot) - Number(b.slot));
    for (let slot = 1; slot <= 4; slot += 1) {
      updateSlot(slot, state.tasks.find((task) => Number(task.slot) === slot) || { slot, status: "waiting" });
    }
    const active = state.tasks.find((task) => Number(task.slot) === Number(state.activeSlot))
      || state.tasks.find((task) => task.status === "completed")
      || state.tasks[0]
      || { slot: 1, status: "waiting" };
    state.activeSlot = Number(active.slot) || 1;
    const activeStatus = active.status || "waiting";
    nodes.stageLabel.textContent = optionLabel(state.activeSlot);
    nodes.stageStatus.textContent = statusLabel(activeStatus);
    nodes.stageRetry.hidden = activeStatus !== "failed";
    nodes.stageRetry.dataset.stageRetry = String(state.activeSlot);
    nodes.stageLoader.hidden = !["waiting", "queued", "processing"].includes(activeStatus);
    if (active.preview_url || (isLocalDemo && activeStatus === "completed")) {
      nodes.stageImage.src = active.preview_url || themeAsset(state.product.image || `product-${handle}-v1.png`);
      nodes.stageImage.hidden = false;
      nodes.stageEmpty.hidden = true;
    } else {
      nodes.stageImage.hidden = true;
      nodes.stageEmpty.hidden = false;
      nodes.stageEmpty.textContent = activeStatus === "failed"
        ? copy("product.retry_one", "This option needs another try.")
        : copy("product.preparing", "Four independent previews are being prepared.");
    }
    const selected = state.tasks.find((task) => task.selected || task.task_id === state.selectedTaskId);
    nodes.confirm.disabled = !selected;
  }

  function demoStart() {
    state.sessionId = `demo_${Date.now()}`;
    state.sessionToken = "local-demo";
    state.tasks = [1, 2, 3, 4].map((slot) => ({ task_id: `demo_task_${slot}`, slot, status: "processing", progress: 30 }));
    saveSession();
    renderTasks(state.tasks);
    setTimeout(() => {
      state.tasks = state.tasks.map((task) => ({ ...task, status: "completed", progress: 100, preview_url: themeAsset(state.product.gallery?.[task.slot - 1] || state.product.image || `product-${handle}-v1.png`) }));
      renderTasks(state.tasks);
      setStatus(copy("preview.title", "Four local preview slots are ready."));
    }, 900);
  }

  async function createAndUploadSession() {
    const product = state.product;
    let session;
    try {
      session = await api("/api/shopify/customization/sessions", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          shop: location.host,
          catalog_id: product.id,
          product_handle: product.handle,
          product_type: product.product_type,
          locale,
          content_type: state.file.type,
          file_size: state.file.size,
          turnstile_token: state.turnstileToken,
          security_challenge: state.securityChallenge,
          security_proof: state.securityProof,
        }),
      });
    } catch (error) {
      throw stagedError("v3.connection_session", "We could not create a secure preview session. Your photo was not uploaded. Please retry.", error);
    }
    state.sessionId = session.session_id;
    state.sessionToken = session.session_token;
    try {
      await fetch(session.upload_url, {
        method: "PUT",
        headers: { "content-type": state.file.type },
        body: state.file,
      }).then((response) => {
        if (!response.ok) throw new TypeError(`Private upload failed (${response.status}).`);
      });
      await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}/uploaded`, {
        method: "POST",
        headers: authHeaders(),
        body: "{}",
      });
    } catch (error) {
      state.sessionId = "";
      state.sessionToken = "";
      throw stagedError("v3.connection_upload", "The private photo upload could not finish. Your photo was not sent to generation. Please retry.", error);
    }
    saveSession();
  }

  async function startGeneration() {
    if (!state.file || !state.product) return;
    nodes.generate.disabled = true;
    nodes.generate.textContent = `${copy("product.generate", "Choose from four online previews")}…`;
    nodes.dialog.showModal();
    renderTasks([1, 2, 3, 4].map((slot) => ({ slot, status: "queued" })));
    try {
      if (isLocalDemo) {
        demoStart();
        return;
      }
      if (!state.sessionId) await createAndUploadSession();
      state.batchKey ||= stableKey(`batch:${state.sessionId}`);
      let payload;
      try {
        payload = await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}/generations`, {
          method: "POST",
          headers: authHeaders(state.batchKey),
          body: JSON.stringify({
            model: "gpt-image-2-2K",
            style_id: currentStyle(),
            options: {
              customer_note: nodes.note?.value.trim() || "",
              size: document.querySelector('input[name="size"]:checked')?.value || "6 cm",
              configuration: currentConfiguration().label,
              people_count: currentConfiguration().people,
              pet_count: currentConfiguration().pets,
              generation_mode: state.generationMode,
            },
          }),
        });
      } catch (error) {
        throw stagedError("v3.connection_generation", "The four-preview request could not start. Your existing upload is preserved; retry when the connection is stable.", error);
      }
      renderTasks(payload.tasks || []);
      beginPolling();
    } catch (error) {
      if (nodes.dialog?.open) nodes.dialog.close();
      const message = !error.formOfUsStage && error instanceof TypeError && /fetch/i.test(error.message)
        ? copy("v3.connection_error", "The preview service could not be reached from this page. Open the published storefront and try again, or contact support if the published site also fails.")
        : error.message;
      setStatus(message, true);
      nodes.generate.disabled = false;
      nodes.generate.textContent = copy("product.generate", "Choose from four online previews");
    }
  }

  async function refreshSession() {
    let payload = await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}`, {
      headers: authHeaders(),
    });
    const pending = (payload.tasks || []).filter((task) => !["completed", "failed"].includes(task.status));
    if (pending.length) {
      await Promise.allSettled(pending.map((task) => api(
        `/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}/tasks/${encodeURIComponent(task.task_id)}`,
        { headers: authHeaders() },
      )));
      payload = await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}`, {
        headers: authHeaders(),
      });
    }
    renderTasks(payload.tasks || []);
    const terminal = (payload.tasks || []).every((task) => ["completed", "failed"].includes(task.status));
    if (terminal) {
      clearInterval(state.pollTimer);
      state.pollTimer = null;
      nodes.generate.disabled = false;
      nodes.generate.textContent = copy("product.generate", "Generate four new previews");
      setStatus(payload.status === "partial_failed" ? copy("product.retry_one", "Some previews need another try.") : copy("preview.title", "Four previews are ready."));
    }
  }

  function beginPolling() {
    clearInterval(state.pollTimer);
    refreshSession().catch(() => {});
    state.pollTimer = setInterval(() => refreshSession().catch((error) => setStatus(error.message, true)), 2500);
  }

  async function retrySlot(slot) {
    const task = state.tasks.find((candidate) => Number(candidate.slot) === Number(slot));
    if (!task) return;
    if (isLocalDemo) {
      const updated = state.tasks.map((candidate) => Number(candidate.slot) === Number(slot) ? { ...candidate, status: "completed" } : candidate);
      renderTasks(updated);
      return;
    }
    state.retryKeys[task.task_id] ||= stableKey(`retry:${task.task_id}`);
    const payload = await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}/tasks/${encodeURIComponent(task.task_id)}/retry`, {
      method: "POST",
      headers: authHeaders(state.retryKeys[task.task_id]),
      body: "{}",
    });
    renderTasks(state.tasks.map((candidate) => candidate.task_id === task.task_id ? payload.task || payload : candidate));
    beginPolling();
  }

  async function selectSlot(slot) {
    const task = state.tasks.find((candidate) => Number(candidate.slot) === Number(slot));
    if (!task || task.status !== "completed") return;
    state.activeSlot = Number(slot);
    if (!isLocalDemo) {
      await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}/select`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ task_id: task.task_id }),
      });
    }
    state.selectedTaskId = task.task_id;
    renderTasks(state.tasks.map((candidate) => ({ ...candidate, selected: candidate.task_id === task.task_id })));
  }

  async function confirmSelection() {
    if (!state.selectedTaskId) return;
    try {
      let properties = {
        _customization_session_id: state.sessionId,
        _customization_task_id: state.selectedTaskId,
        _customization_confirmed: "true",
        _customization_locale: locale,
        _customization_catalog_id: state.product.id,
      };
      if (!isLocalDemo) {
        const payload = await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}/added-to-cart`, {
          method: "POST",
          headers: authHeaders(),
          body: "{}",
        });
        properties = payload.line_item_properties;
      }
      const form = document.querySelector('form[action*="/cart/add"]');
      if (form) {
        Object.entries(properties).forEach(([name, value]) => {
          let field = form.querySelector(`input[name="properties[${CSS.escape(name)}]"]`);
          if (!field) {
            field = document.createElement("input");
            field.type = "hidden";
            field.name = `properties[${name}]`;
            form.append(field);
          }
          field.value = value;
        });
      }
      nodes.dialog.close();
      setStatus(copy("product.cart", "Preview selected. Your customization is ready to add to cart."));
      nodes.generate.textContent = copy("preview.confirm", "Selected · Ready for cart");
      if (nodes.cartButton) nodes.cartButton.disabled = false;
      if (nodes.buyNow) nodes.buyNow.disabled = false;
    } catch (error) {
      setStatus(error.message, true);
    }
  }

  function showHistory() {
    const saved = JSON.parse(localStorage.getItem("formOfUsCustomization") || "null");
    nodes.historyList.innerHTML = saved
      ? `<article class="history-item"><div><strong>${saved.productHandle}</strong><br><small>${new Date(saved.createdAt).toLocaleString()}</small></div><button type="button" data-restore-session>Restore</button></article>`
      : `<p>${copy("product.history_empty", "No saved session on this device yet.")}</p>`;
    nodes.historyDialog.showModal();
  }

  async function restoreHistory() {
    const saved = JSON.parse(localStorage.getItem("formOfUsCustomization") || "null");
    if (!saved || saved.productHandle !== handle) return;
    state.sessionId = saved.sessionId;
    state.sessionToken = saved.sessionToken;
    if (isLocalDemo) {
      demoStart();
      nodes.historyDialog.close();
      nodes.dialog.showModal();
      return;
    }
    await refreshSession();
    nodes.historyDialog.close();
    nodes.dialog.showModal();
    beginPolling();
  }

  function acceptConfirmedPhoto(file, mode = "enhanced") {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 10 * 1024 * 1024) {
      nodes.input.value = "";
      setStatus(copy("product.upload_help", "Please choose a JPG, PNG or WEBP image under 10 MB."), true);
      return;
    }
    if (state.sourceObjectUrl) URL.revokeObjectURL(state.sourceObjectUrl);
    state.file = file;
    state.generationMode = mode === "quick" ? "quick" : "enhanced";
    state.sourceObjectUrl = URL.createObjectURL(file);
    nodes.sourcePreview.src = state.sourceObjectUrl;
    nodes.sourcePreview.hidden = false;
    nodes.uploadCopy.hidden = true;
    updateGenerateAvailability();
    setStatus(isLocalDemo || state.turnstileToken || state.securityReady
      ? copy("product.generate", "Photo confirmed. Choose your previews when you are ready.")
      : copy("product.security", "Photo confirmed. Preparing a secure preview session."));
    if (isLocalDemo || state.turnstileToken || state.securityReady) {
      startGeneration();
    } else {
      state.autoStartRequested = true;
    }
  }

  function bindEvents() {
    nodes.input.addEventListener("formofus:photo-confirmed", (event) => acceptConfirmedPhoto(event.detail?.file, event.detail?.mode));
    if (!document.querySelector("[data-upload-review-dialog]")) {
      nodes.input.addEventListener("change", () => acceptConfirmedPhoto(nodes.input.files?.[0]));
    }
    if (window.FORM_OF_US_CONFIRMED_PHOTO) {
      acceptConfirmedPhoto(window.FORM_OF_US_CONFIRMED_PHOTO);
      window.FORM_OF_US_CONFIRMED_PHOTO = null;
    }
    document.addEventListener("change", (event) => {
      if (event.target.matches('input[name="style"]')) nodes.selectedStyle.textContent = currentStyle();
      if (event.target.matches('input[name="size"]')) nodes.selectedSize.textContent = event.target.value;
      if (event.target.matches('input[name="configuration"]') && nodes.selectedConfiguration) nodes.selectedConfiguration.textContent = event.target.value;
    });
    nodes.generate.addEventListener("click", startGeneration);
    nodes.previewGrid.addEventListener("click", (event) => {
      const retry = event.target.closest("[data-retry-slot]");
      const select = event.target.closest("[data-select-slot]");
      const card = event.target.closest("[data-slot]");
      if (retry) retrySlot(retry.dataset.retrySlot).catch((error) => setStatus(error.message, true));
      if (select) selectSlot(select.dataset.selectSlot).catch((error) => setStatus(error.message, true));
      if (card && !retry && !select) selectSlot(card.dataset.slot).catch((error) => setStatus(error.message, true));
    });
    nodes.previewGrid.addEventListener("keydown", (event) => {
      const card = event.target.closest("[data-slot]");
      if (card && ["Enter", " "].includes(event.key)) {
        event.preventDefault();
        selectSlot(card.dataset.slot).catch((error) => setStatus(error.message, true));
      }
    });
    nodes.stageRetry.addEventListener("click", () => {
      retrySlot(nodes.stageRetry.dataset.stageRetry).catch((error) => setStatus(error.message, true));
    });
    nodes.confirm.addEventListener("click", confirmSelection);
    document.querySelector("[data-preview-close]").addEventListener("click", () => nodes.dialog.close());
    document.querySelector("[data-generate-again]").addEventListener("click", () => {
      state.sessionId = "";
      state.sessionToken = "";
      state.batchKey = "";
      state.tasks = [];
      nodes.dialog.close();
      if (isLocalDemo) {
        startGeneration();
        return;
      }
      state.turnstileToken = "";
      state.securityChallenge = "";
      state.securityProof = null;
      state.securityReady = false;
      if (window.turnstile && state.turnstileWidgetId !== "") window.turnstile.reset(state.turnstileWidgetId);
      else prepareFallbackSecurity().catch((error) => setStatus(error.message, true));
      updateGenerateAvailability();
      setStatus(copy("product.security", "Complete the security check again before generating a new set of four previews."));
    });
    document.querySelector("[data-history-button]").addEventListener("click", showHistory);
    document.querySelector("[data-history-close]").addEventListener("click", () => nodes.historyDialog.close());
    nodes.historyList.addEventListener("click", (event) => {
      if (event.target.closest("[data-restore-session]")) restoreHistory().catch((error) => setStatus(error.message, true));
    });
  }

  async function initialize() {
    state.dictionary = await loadDictionary();
    applyLocaleCopy();
    let catalog = window.FORM_OF_US_CATALOG;
    if (!catalog) {
      const response = await fetch("/catalog/products.json");
      catalog = await response.json();
    }
    const product = catalog.products.find((candidate) => candidate.handle === handle) || catalog.products[0];
    renderProduct(product);
    const localeSelect = document.querySelector("[data-locale-select]");
    localeSelect.value = locale;
    localeSelect.addEventListener("change", () => {
      localStorage.setItem("formOfUsLocale", localeSelect.value);
      location.reload();
    });
    if (product.product_type === "premium_gift_box") return;
    bindEvents();
    updateGenerateAvailability();
    initializeTurnstile().catch((error) => {
      const message = stagedError("v3.connection_security", "The secure preview check is being prepared automatically. Please wait a moment and retry.", error).message;
      setStatus(message, true);
      updateGenerateAvailability();
    });
  }

  initialize().catch((error) => setStatus(error.message, true));
})();
